/**
 * StripeCardForm
 * Must be rendered inside an <Elements> provider.
 */
import { useState } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { Button, makeStyles } from '@material-ui/core';

// Mirrors the Stripe Elements `appearance.rules` defined in StripeCardModal.js
// so the cardholder name field visually matches the card number / ZIP inputs.
const useStyles = makeStyles((theme) => ({
  holderNameField: {
    marginBottom: '12px',
    fontFamily: '"DM Sans", system-ui, sans-serif',
    textAlign: 'left'
  },
  holderNameLabel: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: 500,
    fontSize: '13px',
    color: '#374151',
  },
  holderNameInput: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1.5px solid #e5e7eb',
    borderRadius: '10px',
    padding: '8px 14px',
    fontFamily: 'inherit',
    fontSize: '16px',
    color: '#111827',
    backgroundColor: '#ffffff',
    outline: 'none',
    boxShadow: 'none',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:focus': {
      border: '1.5px solid #2563eb',
      boxShadow: '0 0 0 3px rgba(37,99,235,0.12)',
    },
    '&:disabled': {
      backgroundColor: '#f9fafb',
      color: '#9ca3af',
      cursor: 'not-allowed',
    },
  },
  holderNameInputError: {
    border: '1.5px solid #dc2626',
    '&:focus': {
      border: '1.5px solid #dc2626',
      boxShadow: '0 0 0 3px rgba(220,38,38,0.12)',
    },
  },
  holderNameErrorText: {
    marginTop: '6px',
    fontSize: '12px',
    color: '#dc2626',
  },
  // Wraps <PaymentElement> so we can mask Stripe's bottom-right badge
  // (Stripe Link chip / "Powered by Stripe") that renders outside the iframe.
  paymentElementWrap: {
    position: 'relative',
    '& a[href*="stripe.com"]': {
      display: 'none !important',
    },
    '& a[href*="link.com"]': {
      display: 'none !important',
    },
    '& [class*="PoweredByStripe"]': {
      display: 'none !important',
    },
    '& [class*="powered-by"]': {
      display: 'none !important',
    },
    '& [class*="LinkButton"]': {
      display: 'none !important',
    },
  },
  consentNote: {
    marginTop: '24px'
  },
  errorBox: {
    marginTop: '16px'
  },
  stripeNote: {
    marginTop: '16px'
  },
  saveBtn: {
    [theme.breakpoints.down('md')]: {
      marginBottom: '60px',
    },
  }
}));

export default function StripeCardForm(props) {
  const { step, error, onSave } = props;
  const classes = useStyles();
  const stripe = useStripe();
  const elements = useElements();

  const [nickname, setNickname] = useState('');

  const isSaving = step === 'saving';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || isSaving) return;

    onSave({ stripe, elements, nickname: nickname.trim() });
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className={classes.holderNameField}>
        <label htmlFor="card-nickname" className={classes.holderNameLabel}>
          Nickname
        </label>
        <input
          id="card-nickname"
          type="text"
          className={classes.holderNameInput}
          placeholder="e.g. Personal Visa (optional)"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          disabled={isSaving}
          autoComplete="off"
        />
      </div>

      <div className={classes.paymentElementWrap}>
        <PaymentElement
          options={{
            layout: 'tabs',
            // Only show card (no Apple/Google Pay for a save-card flow)
            // Remove this to allow all payment methods
            paymentMethodOrder: ['card'],
            // Suppress Apple/Google Pay UI in the PaymentElement
            wallets: {
              applePay: 'never',
              googlePay: 'never',
              link: "never",
            },
            fields: {
              billingDetails: {
                name: 'auto', // let Stripe handle name collection (no separate field)
                // email: 'never', // already have the user's email
              },
            },
            terms: { card: 'never' }, // hide Stripe's own terms (add your own if needed)
          }}
        />
      </div>

      <p className={classes.consentNote}>
        By saving, you authorize us to charge this card for future payments.
        You can remove it at any time.
      </p>

      {/* {error && (
        <div className={classes.errorBox} role="alert">
          {error}
        </div>
      )} */}

      <Button
        type="submit"
        disabled={!stripe || isSaving}
        color="primary"
        variant="outlined"
        className={classes.saveBtn}
      >
        {isSaving ? 'Saving…' : 'Save card'}
      </Button>

      {/* <div className={classes.stripeNote}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        Secured by <strong>Stripe</strong>. We never store your raw card data.
      </div> */}
    </form>
  );
}
